import { ModuleNode, ModulePermission } from '../models/app-access.model';

/** moduleCode -> set of permission ids. The working copy behind the matrix. */
export type PermissionMap = Map<string, Set<number>>;

export type CheckState = 'checked' | 'unchecked' | 'indeterminate';

// ---------------------------------------------------------------------
// Module tree
// ---------------------------------------------------------------------

function toNode(item: any): ModuleNode {
  const children = Array.isArray(item?.children ?? item?.Children) ? item.children ?? item.Children : [];
  return {
    code: String(item?.code ?? item?.Code ?? '').trim(),
    name: String(item?.name ?? item?.Name ?? '').trim(),
    route: item?.route ?? item?.Route ?? null,
    parentCode: item?.parentCode ?? item?.ParentCode ?? null,
    sortOrder: Number(item?.sortOrder ?? item?.SortOrder ?? 0) || 0,
    children: children.map(toNode),
  };
}

function sortTree(nodes: ModuleNode[]): ModuleNode[] {
  nodes.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  nodes.forEach((n) => sortTree(n.children));
  return nodes;
}

/**
 * Accepts either an already nested tree (items with `children`) or a flat
 * list (items with `parentCode`) and returns a sorted nested tree.
 */
export function buildModuleTree(items: any[]): ModuleNode[] {
  const nodes = items.map(toNode);

  // Nested input: roots already carry their children.
  if (nodes.some((n) => n.children.length > 0)) {
    return sortTree(nodes);
  }

  // Flat input: link children to parents by parentCode.
  const byCode = new Map(nodes.map((n) => [n.code, n]));
  const roots: ModuleNode[] = [];
  for (const node of nodes) {
    const parent = node.parentCode ? byCode.get(node.parentCode) : undefined;
    if (parent && parent !== node) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return sortTree(roots);
}

/** Depth-first list of every node, each with its depth (for indenting rows). */
export function flattenTree(
  nodes: ModuleNode[],
  depth = 0,
): { node: ModuleNode; depth: number }[] {
  return nodes.flatMap((node) => [
    { node, depth },
    ...flattenTree(node.children, depth + 1),
  ]);
}

/** The node and all of its descendants. */
export function collectDescendants(node: ModuleNode): ModuleNode[] {
  return [node, ...node.children.flatMap(collectDescendants)];
}

// ---------------------------------------------------------------------
// Permission map
// ---------------------------------------------------------------------

export function toPermissionMap(modules: ModulePermission[]): PermissionMap {
  const map: PermissionMap = new Map();
  for (const m of modules) {
    if (m.permissionIds.length) {
      map.set(m.moduleCode, new Set(m.permissionIds));
    }
  }
  return map;
}

export function clonePermissionMap(source: PermissionMap): PermissionMap {
  return new Map([...source].map(([code, ids]) => [code, new Set(ids)]));
}

export function hasPermission(map: PermissionMap, moduleCode: string, permissionId: number): boolean {
  return map.get(moduleCode)?.has(permissionId) ?? false;
}

/**
 * Checks or unchecks one permission on a module. With cascade = true the
 * same change is applied to every descendant. Mutates `map`.
 */
export function setPermission(
  map: PermissionMap,
  node: ModuleNode,
  permissionId: number,
  checked: boolean,
  cascade = true,
): void {
  const targets = cascade ? collectDescendants(node) : [node];
  for (const target of targets) {
    const ids = map.get(target.code) ?? new Set<number>();
    if (checked) ids.add(permissionId);
    else ids.delete(permissionId);

    if (ids.size) map.set(target.code, ids);
    else map.delete(target.code);
  }
}

/**
 * State of a matrix cell. A parent is "indeterminate" when only some of its
 * descendants have the permission.
 */
export function getCheckState(map: PermissionMap, node: ModuleNode, permissionId: number): CheckState {
  if (!node.children.length) {
    return hasPermission(map, node.code, permissionId) ? 'checked' : 'unchecked';
  }

  const all = collectDescendants(node);
  const count = all.filter((n) => hasPermission(map, n.code, permissionId)).length;
  if (count === 0) return 'unchecked';
  return count === all.length ? 'checked' : 'indeterminate';
}

// ---------------------------------------------------------------------
// Save payload
// ---------------------------------------------------------------------

function sameSet(a: Set<number>, b: Set<number>): boolean {
  return a.size === b.size && [...a].every((v) => b.has(v));
}

/**
 * Only the modules whose permissions differ from the saved state, ready for
 * PUT /users/permissions. A module that lost all its permissions is sent with
 * an empty list, which the API treats as "remove". Untouched modules are
 * left out so they can never be overwritten by accident.
 */
export function diffPermissions(original: PermissionMap, current: PermissionMap): ModulePermission[] {
  const codes = new Set([...original.keys(), ...current.keys()]);
  const changes: ModulePermission[] = [];

  for (const moduleCode of codes) {
    const before = original.get(moduleCode) ?? new Set<number>();
    const after = current.get(moduleCode) ?? new Set<number>();
    if (!sameSet(before, after)) {
      changes.push({ moduleCode, permissionIds: [...after].sort((a, b) => a - b) });
    }
  }
  return changes;
}

/** Modules the save would remove entirely (for the confirm dialog). */
export function removedModules(changes: ModulePermission[]): string[] {
  return changes.filter((c) => c.permissionIds.length === 0).map((c) => c.moduleCode);
}